import type { AnnotationEditorRenderEventData } from "@markerjs/markerjs-ui";
import { AnnotationEditor } from "@markerjs/markerjs-ui";
import { Modal, Spin, Typography } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { getFileContent } from "tm-shared/api";
import { createUniqueId } from "tm-shared/utils";

import { UploadPhotoItem } from "@/types/common";

import { StyledCenteredBox, StyledEditorContainer } from "./Styled";

const DEFAULT_MIME = "image/jpeg";

// ─── Utils ─────────────────────────────────────────

const detectMimeType = (base64: string): string => {
    if (base64.startsWith("/9j/")) return "image/jpeg";
    if (base64.startsWith("iVBOR")) return "image/png";
    if (base64.startsWith("R0lGOD")) return "image/gif";
    if (base64.startsWith("UklGR")) return "image/webp";
    return DEFAULT_MIME;
};

const normalizeBase64ToDataUrl = (base64: string): string => {
    if (base64.startsWith("data:image")) return base64;
    return `data:${detectMimeType(base64)};base64,${base64}`;
};

const dataURLtoFile = (dataUrl: string, filename: string): File => {
    const [meta, base64] = dataUrl.split(",");
    const mime = meta.match(/data:(.*?);base64/)?.[1] ?? DEFAULT_MIME;

    const bytes = new Uint8Array(
        atob(base64)
            .split("")
            .map((c) => c.charCodeAt(0)),
    );

    return new File([bytes], filename, { type: mime });
};

const readFileAsDataURL = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsDataURL(file);
    });

interface PhotoEditorModalProps {
    item: UploadPhotoItem;
    index: number;
    onClose: () => void;
    onSave: (file: File, index: number) => void;
}

export const PhotoEditorModal: FC<PhotoEditorModalProps> = ({
    item,
    index,
    onClose,
    onSave,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    const fileNameRef = useRef(`edited-${index}.jpg`);

    const onCloseRef = useRef(onClose);
    const onSaveRef = useRef(onSave);
    const indexRef = useRef(index);

    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        onCloseRef.current = onClose;
    }, [onClose]);

    useEffect(() => {
        onSaveRef.current = onSave;
    }, [onSave]);

    useEffect(() => {
        indexRef.current = index;
    }, [index]);

    // ─── Load image ─────────────────────────────────

    useEffect(() => {
        let mounted = true;

        const load = async () => {
            try {
                setLoading(true);
                setError(null);

                let dataUrl: string;

                if (item.kind === "file") {
                    fileNameRef.current =
                        item.file.name || `edited-${createUniqueId()}.jpg`;

                    dataUrl = await readFileAsDataURL(item.file);
                } else {
                    fileNameRef.current = `edited-${createUniqueId()}.jpg`;

                    const response = await getFileContent({
                        filePath: item.url,
                    });

                    dataUrl = normalizeBase64ToDataUrl(response.result);
                }

                if (!mounted) return;

                setImageSrc(dataUrl);
            } catch {
                if (!mounted) return;
                setError("Не вдалося завантажити зображення");
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();

        return () => {
            mounted = false;
        };
    }, [item]);

    // ─── Init markerjs ──────────────────────────────

    useEffect(() => {
        if (!containerRef.current || !imageSrc) return;

        const container = containerRef.current;
        const editor = new AnnotationEditor();
        let currentEditor: AnnotationEditor | null = editor;

        const img = new Image();

        const handleSave = (
            e: CustomEvent<AnnotationEditorRenderEventData>,
        ) => {
            if (!e.detail.dataUrl) {
                onCloseRef.current();
                return;
            }

            try {
                const file = dataURLtoFile(
                    e.detail.dataUrl,
                    fileNameRef.current,
                );
                onSaveRef.current(file, indexRef.current);
            } catch {
                console.error("Failed to convert dataUrl to File");
            } finally {
                onCloseRef.current();
            }
        };

        const handleClose = () => onCloseRef.current();

        img.onload = () => {
            if (!currentEditor) return;

            currentEditor.targetImage = img;
            currentEditor.style.width = "100%";
            currentEditor.style.height = "100%";
            currentEditor.style.display = "block";

            currentEditor.addEventListener("editorsave", handleSave);
            currentEditor.addEventListener("editorclose", handleClose);

            container.appendChild(currentEditor);
        };

        img.src = imageSrc;

        return () => {
            if (currentEditor) {
                currentEditor.removeEventListener("editorsave", handleSave);
                currentEditor.removeEventListener("editorclose", handleClose);

                if (container.contains(currentEditor)) {
                    container.removeChild(currentEditor);
                }
                currentEditor = null;
            } else {
                container.innerHTML = "";
            }
        };
    }, [imageSrc]);

    return (
        <Modal
            open
            onCancel={onClose}
            title="Редагування фото"
            footer={null}
            centered
            destroyOnClose
            width={900}
        >
            {loading && (
                <StyledCenteredBox>
                    <Spin size="large" />
                    <Typography.Text type="secondary">
                        Завантаження зображення…
                    </Typography.Text>
                </StyledCenteredBox>
            )}

            {error && (
                <StyledCenteredBox>
                    <Typography.Text type="danger">{error}</Typography.Text>
                </StyledCenteredBox>
            )}

            {!loading && !error && imageSrc && (
                <StyledEditorContainer>
                    <div
                        ref={containerRef}
                        style={{
                            width: "100%",
                            maxHeight: "70vh",
                            overflow: "auto",
                        }}
                    />
                </StyledEditorContainer>
            )}
        </Modal>
    );
};
